import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { useSEO } from "@/lib/useSEO";

const DELETE_EMAIL = "theflowstatemethodllc@gmail.com";

export default function DataDeletion() {
  useSEO({
    title: "Data & Account Deletion — The Flow State Method LLC",
    description: "How to request deletion of your account and personal data from the Ronin Box / Better Through Boxing app and website by The Flow State Method LLC.",
    path: "/data-deletion",
  });

  const mailto = `mailto:${DELETE_EMAIL}?subject=${encodeURIComponent(
    "Account & Data Deletion Request"
  )}&body=${encodeURIComponent(
    "Please delete my account and associated personal data.\n\nAccount email: \nName: \n\n(Requests are completed within 30 days.)"
  )}`;

  return (
    <div className="App relative">
      <SiteHeader />
      <main className="max-w-3xl mx-auto px-6 md:px-10 py-16" data-testid="data-deletion-page">
        <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-3">Legal</div>
        <h1 className="font-display uppercase text-4xl md:text-5xl tracking-tight mb-2">Account &amp; Data Deletion</h1>
        <p className="text-zinc-500 text-sm">The Flow State Method LLC · "Better Through Boxing" &amp; the "Ronin Box" app</p>

        <section className="prose prose-invert max-w-none text-zinc-300 leading-relaxed space-y-6 text-sm mt-8">

          <Block num="1." title="How to request deletion">
            <p className="mb-3">You can request deletion of your account and associated personal data at any time. Send us a request and we&apos;ll take care of it — no cost, no phone call required.</p>
            <a
              href={mailto}
              className="inline-block bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest text-xs px-5 py-3 rounded-sm transition-colors"
              data-testid="data-deletion-request-btn"
            >
              Request deletion by email
            </a>
            <p className="mt-3 text-zinc-500">
              Or email us directly at{" "}
              <a href={`mailto:${DELETE_EMAIL}`} className="text-[#FF331F] hover:underline">{DELETE_EMAIL}</a>{" "}
              with the subject <strong className="text-zinc-300">"Account &amp; Data Deletion Request"</strong>. Please send from (or include) the email address on your account so we can verify it&apos;s you.
            </p>
          </Block>

          <Block num="2." title="What gets deleted">
            <p className="mb-3">Once your identity is verified, we permanently delete the personal data associated with your account, including:</p>
            <ul className="list-disc ml-5 space-y-1">
              <li>Your account profile — name, email, phone number, password hash, and profile details (country, ZIP/postal code, skill level).</li>
              <li>Your Ronin Box workout history and stats (rounds, punches, streaks, personal bests).</li>
              <li>Booking and session records tied to your account.</li>
              <li>Any stories, photos, or content you submitted.</li>
              <li>Newsletter subscription and marketing preferences.</li>
              <li>Any push-notification device token associated with your account.</li>
            </ul>
          </Block>

          <Block num="3." title="What may be retained">
            We may retain a limited set of records where the law requires it or for legitimate business needs — for example, payment and transaction records needed for tax, accounting, fraud-prevention, or legal-compliance purposes. These are kept only as long as required and are not used to contact or market to you. Payment card details are never stored by us (they are held only by our payment processor, Stripe).
          </Block>

          <Block num="4." title="Timeframe">
            We aim to acknowledge deletion requests within 7 days and complete verified deletions within <strong className="text-white">30 days</strong>. We&apos;ll email you to confirm once it&apos;s done.
          </Block>

          <Block num="5." title="Related">
            For full details on what we collect and how we handle it, see our{" "}
            <a href="/privacy" className="text-[#FF331F] hover:underline">Privacy Policy</a>.
          </Block>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function Block({ num, title, children }) {
  return (
    <div>
      <h2 className="font-display uppercase tracking-widest text-base text-white mb-2">{num} {title}</h2>
      <div className="text-zinc-300">{children}</div>
    </div>
  );
}
